/**
 * VinculosAuditoresTab - Hospital-Auditor binding management (Tab 1)
 *
 * Features:
 * - Table with auditor/hospital/region data
 * - 5 filters: Hospital, Auditor, Cidade, Estado, Região
 * - Dual-list transfer modal (select hospital, then move auditores ← →)
 * - Import/Export Excel
 * - Master-only actions
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Trash2, Upload, Download, Info, X, Search, Loader2, ArrowRight, ArrowLeft, User } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import escopoContagemService, {
  type VinculoResponse,
  type DropdownOption,
} from '../../services/escopoContagemService';
import apiClient from '../../services/apiClient';
import { msg } from '../../utils/messages';
import PermissionGate, { usePermissoesTela } from '../PermissionGate';
import { ComboboxSelector } from '../ui/ComboboxSelector';
import { opcoesHospitalBuscavel } from '../../utils/hospitalOptions';

interface AuditorOption {
  id: number;
  nome: string;
  email: string;
}

interface VinculosAuditoresTabProps {
  onRefresh?: () => void;
}

export default function VinculosAuditoresTab({ onRefresh }: VinculosAuditoresTabProps) {
  const { podeCriar, podeExcluir, podeExportar } = usePermissoesTela('ESCOPO_CONTAGEM_VIEW');

  const [vinculos, setVinculos] = useState<VinculoResponse[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchHospital, setSearchHospital] = useState('');
  const [searchAuditor, setSearchAuditor] = useState('');
  const [filterCidade, setFilterCidade] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterRegiao, setFilterRegiao] = useState('');

  // Modals
  const [showVincularModal, setShowVincularModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  // Dual-list modal state
  const [modalHospitalId, setModalHospitalId] = useState<number | ''>('');
  // N114: codigo/cnpj vêm do dropdown para a busca poder casar por eles.
  const [hospitaisList, setHospitaisList] = useState<DropdownOption[]>([]);
  const [allAuditores, setAllAuditores] = useState<AuditorOption[]>([]);
  const [linkedVinculos, setLinkedVinculos] = useState<VinculoResponse[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<number[]>([]);
  const [selectedLinked, setSelectedLinked] = useState<number[]>([]);
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);

  const fetchVinculos = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchHospital) params.search_hospital = searchHospital;
      if (searchAuditor) params.search_auditor = searchAuditor;
      if (filterCidade) params.cidade = filterCidade;
      if (filterEstado) params.estado = filterEstado;
      if (filterRegiao) params.regiao = filterRegiao;
      const data = await escopoContagemService.listarVinculos(params);
      setVinculos(data);
    } catch {
      msg.error('Erro ao carregar vínculos');
    } finally {
      setLoading(false);
    }
  }, [searchHospital, searchAuditor, filterCidade, filterEstado, filterRegiao]);

  useEffect(() => {
    fetchVinculos();
  }, [fetchVinculos]);

  // Derived filter options
  const cidadeOptions = useMemo(() => {
    const set = new Set(vinculos.map(v => v.cidade).filter(Boolean));
    return Array.from(set).sort();
  }, [vinculos]);

  const estadoOptions = useMemo(() => {
    const set = new Set(vinculos.map(v => v.estado).filter(Boolean));
    return Array.from(set).sort();
  }, [vinculos]);

  const regiaoOptions = useMemo(() => {
    const set = new Set(vinculos.map(v => v.regiao).filter(Boolean));
    return Array.from(set).sort();
  }, [vinculos]);

  /**
   * N114 (Alana): opções do combobox de hospital no modal "Vincular".
   *
   * A regra saiu daqui para `utils/hospitalOptions` quando o N133 (inclusão
   * manual no escopo) e o N135 (conferência de lacres) pediram a MESMA busca:
   * três cópias da mesma lógica seria a receita para uma delas divergir e a
   * busca voltar a falhar em uma tela só. O comportamento aqui não mudou.
   */
  const hospitalOptions = useMemo(
    () => opcoesHospitalBuscavel(hospitaisList),
    [hospitaisList],
  );

  // Cidade now filtered server-side via params.cidade

  const handleDesvincular = async (id: number) => {
    if (!confirm('Deseja realmente desvincular este auditor?')) return;
    try {
      await escopoContagemService.deletarVinculo(id);
      msg.success('Auditor desvinculado');
      fetchVinculos();
    } catch {
      msg.error('Erro ao desvincular');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await escopoContagemService.exportVinculos();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vinculos_auditor_hospital.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      msg.error('Erro ao exportar');
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const result = await escopoContagemService.importVinculos(importFile);
      msg.success(`Importação concluída: ${result.created || 0} criados, ${result.updated || 0} atualizados`);
      setShowImportModal(false);
      setImportFile(null);
      fetchVinculos();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro na importação');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await escopoContagemService.templateVinculos();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_vinculos.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      msg.error('Erro ao baixar template');
    }
  };

  // === Dual-list modal logic ===

  const openVincularModal = async () => {
    setModalHospitalId('');
    setAllAuditores([]);
    setLinkedVinculos([]);
    setSelectedAvailable([]);
    setSelectedLinked([]);
    setSearchLeft('');
    setSearchRight('');
    setShowVincularModal(true);

    // Load hospitals list
    try {
      const hosps = await escopoContagemService.dropdownHospitais();
      setHospitaisList(hosps);
    } catch {
      msg.error('Erro ao carregar hospitais');
    }
  };

  // When hospital is selected, load all auditores + linked vinculos for that hospital
  useEffect(() => {
    if (!showVincularModal || !modalHospitalId) {
      setAllAuditores([]);
      setLinkedVinculos([]);
      setSelectedAvailable([]);
      setSelectedLinked([]);
      return;
    }

    const load = async () => {
      setLoadingModal(true);
      try {
        const [audsRes, mastersRes, vincsRes] = await Promise.all([
          apiClient.get('/users', { params: { role: 'AUDITOR', is_active: true } }),
          apiClient.get('/users', { params: { role: 'MASTER', is_active: true } }),
          escopoContagemService.listarVinculos({ hospital_id: String(modalHospitalId) }),
        ]);
        const audsData = [
          ...(Array.isArray(audsRes.data) ? audsRes.data : []),
          ...(Array.isArray(mastersRes.data) ? mastersRes.data : []),
        ];
        setAllAuditores(
          audsData.map((u: any) => ({
            id: u.id,
            nome: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || `Auditor #${u.id}`,
            email: u.email || '',
          }))
        );
        setLinkedVinculos(vincsRes);
      } catch {
        msg.error('Erro ao carregar dados');
      } finally {
        setLoadingModal(false);
      }
    };
    load();
  }, [showVincularModal, modalHospitalId]);

  const linkedAuditorIds = useMemo(() => new Set(linkedVinculos.map(v => v.auditor_id)), [linkedVinculos]);

  const filteredAvailable = useMemo(() => {
    const s = searchLeft.trim().toLowerCase();
    return allAuditores
      .filter(a => !linkedAuditorIds.has(a.id))
      .filter(a => !s || `${a.nome} ${a.email}`.toLowerCase().includes(s))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [allAuditores, linkedAuditorIds, searchLeft]);

  const filteredLinked = useMemo(() => {
    const s = searchRight.trim().toLowerCase();
    if (!s) return linkedVinculos;
    return linkedVinculos.filter(v => `${v.auditor_nome} ${v.auditor_email}`.toLowerCase().includes(s));
  }, [linkedVinculos, searchRight]);

  const toggleAvailableSelection = (id: number) => setSelectedAvailable(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleLinkedSelection = (id: number) => setSelectedLinked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllAvailable = () => setSelectedAvailable(selectedAvailable.length === filteredAvailable.length ? [] : filteredAvailable.map(a => a.id));
  const toggleAllLinked = () => setSelectedLinked(selectedLinked.length === filteredLinked.length ? [] : filteredLinked.map(v => v.id));

  const handleLinkSelected = async () => {
    if (selectedAvailable.length === 0 || !modalHospitalId) return;
    try {
      setLinking(true);
      for (const auditorId of selectedAvailable) {
        await escopoContagemService.criarVinculo(auditorId, Number(modalHospitalId));
      }
      msg.success(`${selectedAvailable.length} auditor(es) vinculado(s)!`);
      setSelectedAvailable([]);
      // Refresh linked list
      const vincsRes = await escopoContagemService.listarVinculos({ hospital_id: String(modalHospitalId) });
      setLinkedVinculos(vincsRes);
      fetchVinculos();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao vincular');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkSelected = async () => {
    if (selectedLinked.length === 0) return;
    try {
      setUnlinking(true);
      for (const vinculoId of selectedLinked) {
        await escopoContagemService.deletarVinculo(vinculoId);
      }
      msg.success(`${selectedLinked.length} auditor(es) desvinculado(s)!`);
      setSelectedLinked([]);
      // Refresh linked list
      const vincsRes = await escopoContagemService.listarVinculos({ hospital_id: String(modalHospitalId) });
      setLinkedVinculos(vincsRes);
      fetchVinculos();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao desvincular');
    } finally {
      setUnlinking(false);
    }
  };

  // === Table columns ===

  const columns = useMemo<MRT_ColumnDef<VinculoResponse>[]>(() => {
    const cols: MRT_ColumnDef<VinculoResponse>[] = [
      { accessorKey: 'auditor_id', header: 'ID Aud.', size: 70 },
      { accessorKey: 'auditor_nome', header: 'Auditor', size: 180 },
      { accessorKey: 'hospital_id', header: 'ID Hosp.', size: 70 },
      { accessorKey: 'hospital_nome', header: 'Hospital', size: 200 },
      { accessorKey: 'hospital_cnpj', header: 'CNPJ', size: 160 },
      { accessorKey: 'cidade', header: 'Cidade', size: 140 },
      { accessorKey: 'estado', header: 'UF', size: 60 },
      { accessorKey: 'regiao', header: 'Região', size: 100 },
    ];
    if (podeExcluir) {
      cols.push({
        id: 'acoes',
        header: 'Ações',
        size: 80,
        Cell: ({ row }) => (
          <button
            onClick={() => handleDesvincular(row.original.id)}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            title="Desvincular"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        ),
      });
    }
    return cols;
  }, [podeExcluir]);

  const table = useMantineReactTable({
    columns,
    data: vinculos,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableDensityToggle: false,
    initialState: { density: 'xs', pagination: { pageIndex: 0, pageSize: 25 } },
    state: { isLoading: loading },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
    },
    mantinePaperProps: { shadow: 'none', withBorder: false },
  });

  return (
    <div>
      {/* Toolbar */}
      {(podeCriar || podeExportar) && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="criar">
            <button
              onClick={openVincularModal}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Vincular
            </button>
          </PermissionGate>
          <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="exportar">
            <button
              onClick={handleExport}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" /> Exportar Excel
            </button>
          </PermissionGate>
          <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="criar">
            <button
              onClick={() => { setImportFile(null); setShowImportModal(true); }}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-1.5"
            >
              <Upload className="w-4 h-4" /> Importar Excel
            </button>
          </PermissionGate>
          <button
            onClick={() => setShowInfoModal(true)}
            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
            title="Info template"
          >
            <Info className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchHospital}
            onChange={e => setSearchHospital(e.target.value)}
            placeholder="Buscar hospital..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchAuditor}
            onChange={e => setSearchAuditor(e.target.value)}
            placeholder="Buscar auditor..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <select
          value={filterCidade}
          onChange={e => setFilterCidade(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">Todas Cidades</option>
          {cidadeOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={filterEstado}
          onChange={e => setFilterEstado(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">Todos Estados</option>
          {estadoOptions.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select
          value={filterRegiao}
          onChange={e => setFilterRegiao(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">Todas Regiões</option>
          {regiaoOptions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Table */}
      <MantineReactTable table={table} />

      {/* Dual-List Transfer Modal */}
      {showVincularModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <User className="w-6 h-6 text-blue-600" />
                Vincular Auditores ao Hospital
              </h2>
              <button onClick={() => setShowVincularModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Hospital selector */}
            <div className="px-6 pt-4">
              {/* N114 (Alana): era um <select>, que não tem busca — achar o
                  hospital dependia de rolar a lista pelo nome. O
                  ComboboxSelector já existe e já suporta busca por `subLabel` e
                  `searchValue`, então dá para procurar por código e CNPJ sem
                  componente novo. */}
              <ComboboxSelector
                label="Selecione o Hospital"
                options={hospitalOptions}
                value={modalHospitalId === '' ? 0 : modalHospitalId}
                onChange={id => setModalHospitalId(id || '')}
                placeholder="Selecione um hospital..."
                searchPlaceholder="Buscar por nome, código ou CNPJ..."
              />
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                Cada auditor pode estar vinculado a múltiplos hospitais.
              </p>
            </div>

            <div className="flex-1 overflow-hidden p-6">
              {!modalHospitalId ? (
                <div className="py-12 flex flex-col items-center justify-center text-gray-500">
                  Selecione um hospital acima para gerenciar os auditores vinculados.
                </div>
              ) : loadingModal ? (
                <div className="py-12 flex flex-col items-center justify-center text-gray-500">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                  Carregando auditores...
                </div>
              ) : (
                <div className="flex gap-4 h-[400px]">
                  {/* Left: Available */}
                  <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Disponíveis</h4>
                        <span className="text-xs font-medium text-gray-500 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">{filteredAvailable.length}</span>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" value={searchLeft} onChange={e => setSearchLeft(e.target.value)} placeholder="Buscar..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
                      </div>
                      {filteredAvailable.length > 0 && (
                        <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                          <input type="checkbox" checked={selectedAvailable.length === filteredAvailable.length && filteredAvailable.length > 0} onChange={toggleAllAvailable} className="rounded border-gray-300 text-blue-600" />
                          Selecionar todos
                        </label>
                      )}
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                      {filteredAvailable.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-sm text-gray-500">{searchLeft ? 'Nenhum encontrado' : 'Todos já vinculados'}</div>
                      ) : filteredAvailable.map(a => (
                        <label key={a.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedAvailable.includes(a.id) ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                          <input type="checkbox" checked={selectedAvailable.includes(a.id)} onChange={() => toggleAvailableSelection(a.id)} className="mt-0.5 rounded border-gray-300 text-blue-600" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{a.nome}</p>
                            <p className="text-xs text-gray-500 truncate">{a.email}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Center Buttons */}
                  <div className="flex flex-col items-center justify-center gap-3 w-28">
                    <button onClick={handleLinkSelected} disabled={selectedAvailable.length === 0 || linking} className="w-full px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center" title="Vincular">
                      {linking ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5 stroke-[2.5]" />}
                    </button>
                    <button onClick={handleUnlinkSelected} disabled={selectedLinked.length === 0 || unlinking} className="w-full px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center justify-center" title="Desvincular">
                      {unlinking ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowLeft className="w-5 h-5 stroke-[2.5]" />}
                    </button>
                  </div>

                  {/* Right: Linked */}
                  <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Vinculados</h4>
                        <span className="text-xs font-medium text-green-700 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded">{filteredLinked.length}</span>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" value={searchRight} onChange={e => setSearchRight(e.target.value)} placeholder="Buscar..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
                      </div>
                      {filteredLinked.length > 0 && (
                        <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                          <input type="checkbox" checked={selectedLinked.length === filteredLinked.length && filteredLinked.length > 0} onChange={toggleAllLinked} className="rounded border-gray-300 text-blue-600" />
                          Selecionar todos
                        </label>
                      )}
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                      {filteredLinked.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-sm text-gray-500">{searchRight ? 'Nenhum encontrado' : 'Nenhum vinculado'}</div>
                      ) : filteredLinked.map(v => (
                        <label key={v.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedLinked.includes(v.id) ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                          <input type="checkbox" checked={selectedLinked.includes(v.id)} onChange={() => toggleLinkedSelection(v.id)} className="mt-0.5 rounded border-gray-300 text-blue-600" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{v.auditor_nome}</p>
                            <p className="text-xs text-gray-500 truncate">{v.auditor_email || '-'}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button type="button" onClick={() => setShowVincularModal(false)} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg">Concluir</button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Importar Vínculos</h3>
              <button onClick={() => setShowImportModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <button onClick={handleDownloadTemplate} className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 underline">
                Baixar template
              </button>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={e => setImportFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowImportModal(false)} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">Cancelar</button>
              <button onClick={handleImport} disabled={!importFile || importing} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                {importing ? 'Importando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Formato do Template</h3>
              <button onClick={() => setShowInfoModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <p>O template Excel deve conter as colunas:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Email Auditor *</strong> — email cadastrado no sistema</li>
                <li><strong>CNPJ Hospital *</strong> — CNPJ do hospital (com ou sem formatação)</li>
              </ul>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-3">
                Cada auditor pode estar vinculado a múltiplos hospitais simultaneamente.
              </p>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowInfoModal(false)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
